import type { ReactElement } from 'react';
import type { MintedOriginOfShellNft } from '@/features/spirit/requests';

import React, { Suspense } from 'react';
import Head from 'next/head';
import tw, { styled, theme } from 'twin.macro';
import {
  Avatar,
  Heading,
  Button,
  Tag,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  CircularProgress,
  CircularProgressLabel,
} from '@chakra-ui/react';
import Link from 'next/link';
import * as R from 'ramda';
import { atom, useAtomValue } from 'jotai';
import { queryClientAtom } from 'jotai/query';

import { RACE_TYPE_CHAIN_MAP, CAREER_TYPE_CHAIN_MAP } from '@/types/nftAttribute';
import AppLayout from '@/providers/AppLayout';
import ChakraThemeProvider from '@/providers/ChakraThemeProvider';
import { pwapiEndpointPrefixAtom } from '@/features/spirit/atoms/hosts';
import { subqlEndpointAtom } from '@/atoms/subqlEndpointAtom';
import { queryBulkGetOriginShells, querySpiritLeaderboard } from '@/features/spirit/queries';
import { apiPromiseAtom } from '@/features/parachain/atoms';
import { currentAccountAtom, currentProfileAtom } from '@/features/identity/atoms';
import { queryMultiChainIdentity } from '@/features/identity/queries';
import formatDisplayName from '@/features/identity/formatDisplayName';
import trimAddress from '@/features/identity/trimAddress';
import ConditionallyRender from '@/components/ConditionallyRender';
import { spiritAtom } from '@/features/spirit/atoms/spiritAtom';
import LevelIcon from '@/components/LevelIcon';
import RaceAvatar from '@/components/RaceAvatar/RaceRavatar';
import { SpeciesIcon } from '@/components/icons/SpeciesIcon';
import { CareerIcon } from '@/components/icons/CareerIcon';

import IconDown from '../../lib/svgs/down.svg';

//
//
//

const spiritLeaderboardAtom = atom(async (get) => {
  const api = get(apiPromiseAtom)
  const queryClient = get(queryClientAtom);
  const pwApiPRefix = get(pwapiEndpointPrefixAtom)
  const subqlPrefix = get(subqlEndpointAtom)
  const currentAccount = get(currentAccountAtom)
  const spirits = await queryClient.fetchQuery(querySpiritLeaderboard(pwApiPRefix))
  const addresses = R.map(i => i.owner_address, spirits)
  const [shells, identities] = await Promise.all([
    queryClient.fetchQuery(queryBulkGetOriginShells(subqlPrefix, addresses)),
    queryClient.fetchQuery(queryMultiChainIdentity(api, addresses)),
  ])
  const shellsByOwner = R.pipe(
    R.sortBy(R.prop('owner')),
    R.groupBy<MintedOriginOfShellNft>(R.prop('owner')),
  )(shells)
  const displayNameByOwner = R.fromPairs(R.zip(addresses, R.map(formatDisplayName, identities)))
  return R.map(
    i => ({
      ...i,
      isCurrentUser: i.owner_address === currentAccount?.address,
      dressed: R.nth(0, shellsByOwner[i.owner_address] || []) || {},
      shortenAddress: trimAddress(i.owner_address),
      displayName: displayNameByOwner[i.owner_address],
    }),
    spirits
  )
})

const currentUserOriginOfShellsAtom = atom(async (get) => {
  const queryClient = get(queryClientAtom);
  const subqlPrefix = get(subqlEndpointAtom)
  const currentAccount = get(currentAccountAtom)
  if (!currentAccount) {
    return []
  }
  const result = queryClient.fetchQuery(queryBulkGetOriginShells(subqlPrefix, [currentAccount.address]))
  return result
})

//
//
//

interface TableCellProps {
  rank: number;
  isCurrentUser?: boolean;
  displayName: string;
  address: string;
  shortenAddress: string;
  level: number;
  rarity?: string;
  career?: string;
  race?: string;
  dextreity: number;
  willpower: number;
  intelligence: number;
  strength: number;
}

const OverrideCircularProgress = styled(CircularProgress)`
  .chakra-progress__track {
    stroke: #2d323b !important;
  }
`;

const AttributeCircle = ({ children }: { children: number }) => {
  return (
    <OverrideCircularProgress size='12' value={children} thickness='6px' color='brand.500'>
      <CircularProgressLabel>
        <div tw="flex flex-row justify-center items-center text-white text-base">{children}</div>
      </CircularProgressLabel>
    </OverrideCircularProgress> 
  )
}

const FoodRankingTableCell = ({
  rank,
  isCurrentUser,
  displayName,
  address,
  shortenAddress,
  level,
  rarity,
  career,
  race,
  dextreity,
  willpower,
  intelligence,
  strength,
}: TableCellProps) => {
  return (
    <Link href={`/profiles/${address}`} legacyBehavior>
      <Tr bg='rgba(22, 27, 31, 0.8)' rounded='sm' cursor='pointer'>
        <Td>
          <div tw='flex flex-row gap-8 items-center'>
            <div css={[rank > 3 && tw`invisible`]}>
              <LevelIcon level={rank} size={10} colorScheme={rarity?.toLowerCase()} />
            </div>
            <span>{rank}</span>
            {isCurrentUser && <span>YOU</span>}
          </div>
        </Td>
        <Td>
          <div tw='flex flex-row gap-4 items-center'>
            <RaceAvatar size='48px' race={race} rarity={rarity} />
            <div tw='flex flex-col'>
              <h4 tw='text-base'>{displayName}</h4>
              <span tw='text-grey-500 text-sm underline'>{shortenAddress}</span>
            </div>
          </div>
        </Td>
        <Td>
          Lv. <span tw='ml-0.5 font-semibold text-lg'>{level}</span>
        </Td>
        <Td>
          <AttributeCircle>{dextreity}</AttributeCircle>
        </Td>
        <Td>
          <AttributeCircle>{willpower}</AttributeCircle>
        </Td>
        <Td>
          <AttributeCircle>{intelligence}</AttributeCircle>
        </Td>
        <Td>
          <AttributeCircle>{strength}</AttributeCircle>
        </Td>
      </Tr>
    </Link>
  );
};

const HeroHeading = styled.section`
  background: url('/static/leaderboards/heading-bg.jpg') no-repeat 0 0;
  background-size: cover;
  width: 100%;
  height: 240px;
  margin-top: 88px;
`;

const SpiritBackdrop = styled.div`
  ${tw`flex px-8 rounded-sm relative overflow-hidden`}
  height: 128px;
  background: rgba(22, 27, 31, 0.8);
  backdrop-filter: blur(10px);
`

const ShellSelectBackdrop = styled.div`
  ${tw`flex relative rounded-sm overflow-hidden`}
  height: 68px;
  background: linear-gradient(90deg, rgba(248, 136, 27, 0) 56.34%, rgba(248, 80, 27, 0.3) 100%), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), rgba(22, 27, 31, 0.8);
  backdrop-filter: blur(10px);
`

const LeaderboardTable = () => {
  const data = useAtomValue(spiritLeaderboardAtom)
  return (
    <TableContainer>
      <Table variant='unstyled' css={['border-collapse: separate !important; border-spacing: 0 10px;']}>
        <Thead>
          <Tr>
            <Th>Rank</Th>
            <Th>User Info</Th>
            <Th>Spirit Level</Th>
            <Th>Dexterity</Th>
            <Th>Willpower</Th>
            <Th>Intelligence</Th>
            <Th>Strength</Th>
          </Tr>
        </Thead>
        <Tbody>
          {data.map((i, index) => (
            <FoodRankingTableCell
              key={index}
              rank={index + 1}
              isCurrentUser={i.isCurrentUser}
              displayName={i.displayName || i.shortenAddress}
              address={i.owner_address}
              shortenAddress={i.shortenAddress}
              level={i.stats.reputation.level}
              rarity={R.path(['dressed', 'rarity'], i)}
              career={R.path(['dressed', 'career'], i)}
              race={R.path(['dressed', 'race'], i)}
              dextreity={i.stats.dexterity.level}
              willpower={i.stats.willpower.level}
              intelligence={i.stats.intellect.level}
              strength={i.stats.strength.level}
            />
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  )
}

const CurrentUserInfo = () => {
  const currentAccount = useAtomValue(currentAccountAtom)
  const currentProfile = useAtomValue(currentProfileAtom)
  const spirit = useAtomValue(spiritAtom)
  const originShells = useAtomValue(currentUserOriginOfShellsAtom)
  if (!currentAccount) {
    return null
  }
  const picked = R.nth(0, originShells) || ({} as any)
  return (
    <section tw='flex flex-col lg:flex-row gap-5'>
      <div tw='rounded-sm bg-brand flex justify-center items-center h-36 lg:w-[214px] lg:h-[214px]'>
        <div tw='text-black text-center'>
          <h3 tw='text-4xl font-bold font-secondary'>{spirit.rank}</h3>
          <p tw='uppercase text-base'>Your Rank</p>
        </div>
      </div>
      <div tw='flex flex-col gap-5 flex-1'>
        <SpiritBackdrop>
          <div tw='flex flex-row items-center justify-between w-[75%] z-10'>
            <div tw='flex flex-row gap-5 items-center'>
              <RaceAvatar size='80px' race={picked.race} rarity={picked.rarity} />
              <div>
                <h3 tw='text-xl font-medium font-secondary'>{currentProfile.displayName}</h3>
                <span tw='text-base text-gray-500 underline'>{trimAddress(currentAccount.address)}</span>
              </div>
            </div>
            <div tw='flex flex-row gap-16'>
              <div>
                <div tw='text-[rgba(255, 255, 255, 0.75)] text-xs'>SPIRIT NUMBER</div>
                <div tw='text-xl font-bold'>#{spirit.formattedNftId}</div>
              </div>
              <div>
                <div tw='text-[rgba(255, 255, 255, 0.75)] text-xs'>SPIRIT LEVEL</div>
                <div tw='text-xl font-bold'>Lv. {spirit.level}</div>
              </div>
            </div>
          </div>
          <img
            src='/static/spirit/spirit-bg.png'
            alt=''
            tw='absolute right-0 h-[128px] z-0'
          />
        </SpiritBackdrop>

        {originShells.length > 0 && (
          <ShellSelectBackdrop>
            <div tw='flex flex-row justify-between items-center w-full relative z-10'>
              <div tw='px-5 text-xl font-semibold'>YOUR SHELL</div>
              <div tw='px-5 flex flex-row gap-8 items-center'>
                <span>{RACE_TYPE_CHAIN_MAP[picked.race]} #{picked.nftId + 1}</span>
                <span tw='inline-flex gap-2'>
                  <SpeciesIcon race={picked.race} tw='h-6 w-6' />
                  {RACE_TYPE_CHAIN_MAP[picked.race]}
                </span>
                <span tw='inline-flex gap-2'>
                  <CareerIcon career={picked.career} tw='h-6 w-6' />
                  {CAREER_TYPE_CHAIN_MAP[picked.career]}
                </span>
                <span tw='inline-flex gap-2'>
                  <Tag colorScheme={picked.rarity.toLowerCase()} size='sm'>{picked.rarity}</Tag>
                </span>
                {/* <span>
                  <IconDown tw='h-4 w-4 text-white' />
                </span> */}
              </div>
            </div>
            <img
              src={`/static/origins/bg-${picked.race}.png`}
              alt=''
              tw='absolute top-0 left-0 h-[68px] z-0'
            />
          </ShellSelectBackdrop>
        )}
      </div>
    </section>
  )
}

export default function SpiritLeaderBoardPage() {
  return (
    <ChakraThemeProvider>
      <Head>
        <title>Spirit Leaderboard | PhalaWorld</title>
      </Head>
      <ConditionallyRender client>
        <HeroHeading>
          <div tw='grid grid-cols-12 max-w-10xl px-5 xl:mx-0 3xl:mx-auto relative'>
            <header tw="col-start-2 col-span-10 flex flex-col justify-center h-[240px] relative z-10">
              <Heading as='h1' fontSize='52px' fontWeight='700' fontFamily='Orbitron' color='black' textShadow={'#36FAD7 1px 0 10px'}>
                SPIRIT
              </Heading>
              <Heading as='h2' fontSize='36px' fontWeight='700' fontFamily='Orbitron' color='black' textShadow={'#36FAD7 1px 0 10px'}>
                LEADERBOARD
              </Heading>
            </header>
            <img
              src='/static/leaderboards/silhouette-spirit.png'
              alt=''
              tw='absolute top-0 right-0 z-0 pointer-events-none w-[477px] h-[240px]'
            />
          </div>
        </HeroHeading>

        <div tw='grid grid-cols-12 gap-5 max-w-10xl px-5 xl:mx-0 3xl:mx-auto pt-10 pb-10'>
          <div tw="col-start-2 col-span-10">
            <Suspense>
              <CurrentUserInfo />
            </Suspense>
            <Suspense>
              <LeaderboardTable />
            </Suspense>
          </div>
        </div>
      </ConditionallyRender>
    </ChakraThemeProvider>
  );
}

SpiritLeaderBoardPage.getLayout = (page: ReactElement) => {
  return <AppLayout>{page}</AppLayout>;
};
